import {
  PdfScene,
  type PdfOutlineItem,
  type PdfMarkupStyle,
  type PdfMarkupTool,
  type PdfPointerMode,
  type PdfScaleMode,
  type PdfViewerState,
} from "../../core/pdf/pdf-scene";
import type {
  PdfMeasureTool,
  PdfMeasureUnits,
  PdfScale,
} from "../../core/pdf/pdf-measure";
import type { PdfCompareMode } from "../../core/pdf/pdf-compare";

export const PdfHandler = {
  viewer: null as PdfScene | null,

  Start([container, compareContainer, onChange]: [
    HTMLDivElement,
    HTMLDivElement,
    (viewerState: Partial<PdfViewerState>) => void,
  ]) {
    if (!this.viewer) {
      this.viewer = new PdfScene(container, compareContainer, onChange);
    }
  },

  Close() {
    if (this.viewer) {
      this.viewer.Dispose();
      this.viewer = null;
    }
  },

  async Open(url: string, onProgress: (percent: number | null) => void) {
    if (!this.viewer) {
      throw new Error("PDF viewer is not active!");
    }
    return await this.viewer.Open(url, onProgress);
  },

  GoToPage(pageNumber: number) {
    if (this.viewer) {
      this.viewer.GoToPage(pageNumber);
    }
  },

  NextPage() {
    if (this.viewer) {
      this.viewer.NextPage();
    }
  },

  PreviousPage() {
    if (this.viewer) {
      this.viewer.PreviousPage();
    }
  },

  ZoomIn() {
    if (this.viewer) {
      this.viewer.ZoomIn();
    }
  },

  ZoomOut() {
    if (this.viewer) {
      this.viewer.ZoomOut();
    }
  },

  SetScaleMode(scaleMode: PdfScaleMode) {
    if (this.viewer) {
      this.viewer.SetScaleMode(scaleMode);
    }
  },

  Rotate() {
    if (this.viewer) {
      this.viewer.Rotate();
    }
  },

  SetPointerMode(pointerMode: PdfPointerMode) {
    if (this.viewer) {
      this.viewer.SetPointerMode(pointerMode);
    }
  },

  SetMeasureTool(tool: PdfMeasureTool | null) {
    if (this.viewer) {
      this.viewer.SetMeasureTool(tool);
    }
  },

  SetMeasureUnits(units: PdfMeasureUnits) {
    if (this.viewer) {
      this.viewer.measurer.SetUnits(units);
    }
  },

  SetPageScale(pageNumber: number, scale: PdfScale) {
    if (this.viewer) {
      this.viewer.measurer.SetPageScale(pageNumber, scale);
    }
  },

  ApplyCalibration(metres: number) {
    if (this.viewer) {
      this.viewer.measurer.ApplyCalibration(metres);
    }
  },

  CancelCalibration() {
    if (this.viewer) {
      this.viewer.measurer.CancelCalibration();
    }
  },

  RemoveMeasurement(id: number) {
    if (this.viewer) {
      this.viewer.measurer.Remove(id);
    }
  },

  ClearMeasurements() {
    if (this.viewer) {
      this.viewer.measurer.Clear();
    }
  },

  SetMarkupTool(tool: PdfMarkupTool | null) {
    if (this.viewer) {
      this.viewer.SetMarkupTool(tool);
    }
  },

  AddStamp() {
    if (this.viewer) {
      this.viewer.AddStamp();
    }
  },

  SetMarkupStyle(markupStyle: PdfMarkupStyle) {
    if (this.viewer) {
      this.viewer.SetMarkupStyle(markupStyle);
    }
  },

  SetHighlightColor(color: string) {
    if (this.viewer) {
      this.viewer.SetHighlightColor(color);
    }
  },

  async SaveWithMarkup(): Promise<Uint8Array | null> {
    if (!this.viewer) {
      return null;
    }
    return await this.viewer.SaveWithMarkup();
  },

  async OpenCompare(url: string): Promise<number> {
    if (!this.viewer) {
      throw new Error("PDF viewer is not active!");
    }
    return await this.viewer.comparer.Open(url);
  },

  CloseCompare() {
    if (this.viewer) {
      this.viewer.comparer.Close();
    }
  },

  SetCompareMode(mode: PdfCompareMode | null) {
    if (this.viewer) {
      this.viewer.comparer.SetMode(mode);
    }
  },

  SetComparePageOffset(pageOffset: number) {
    if (this.viewer) {
      this.viewer.comparer.SetPageOffset(pageOffset);
    }
  },

  SetCompareFade(fade: number) {
    if (this.viewer) {
      this.viewer.comparer.SetFade(fade);
    }
  },

  async GetData(): Promise<Uint8Array | null> {
    if (!this.viewer) {
      return null;
    }
    return await this.viewer.GetData();
  },

  async RenderPrintPages(
    onProgress: (pagesDone: number, pageCount: number) => void,
  ): Promise<string[]> {
    if (!this.viewer) {
      return [];
    }
    return await this.viewer.RenderPrintPages(onProgress);
  },

  async RenderThumbnail(pageNumber: number, canvas: HTMLCanvasElement) {
    if (this.viewer) {
      await this.viewer.RenderThumbnail(pageNumber, canvas);
    }
  },

  async GetOutline(): Promise<PdfOutlineItem[]> {
    if (!this.viewer) {
      return [];
    }
    return await this.viewer.GetOutline();
  },

  GoToOutlineItem(item: PdfOutlineItem) {
    if (this.viewer) {
      this.viewer.GoToOutlineItem(item);
    }
  },

  Search(query: string, findPrevious = false) {
    if (this.viewer) {
      this.viewer.Search(query, findPrevious);
    }
  },

  ClearSearch() {
    if (this.viewer) {
      this.viewer.ClearSearch();
    }
  },
};
